import { atom } from 'jotai';
import { atomWithReset } from 'jotai/utils';

import { Option } from '@/lib/v2/components';

import { MASTER_ACTION } from '@/src/modules/CampaignsModule/constants';
import { ITableCampaignActions } from '@/src/modules/CampaignsModule/interfaces/CampaignActions';
import {
  ECampaignsTypeOptions,
  ICampaignDetail,
  ICampaignDetailV1,
  IMoveCampaignPayload,
  IStrategy,
  ITableCampaigns,
  ITotalsCampaign,
} from '@/src/modules/CampaignsModule/interfaces/Campaigns';

export const atomCampaignList = atomWithReset<ITableCampaigns[] | undefined>(undefined);
atomCampaignList.debugLabel = 'atomCampaignList';

export const atomCampaignListAll = atomWithReset<ITableCampaigns[] | undefined>([]);
atomCampaignListAll.debugLabel = 'atomCampaignListAll';

export const atomLoadingProcess = atomWithReset<boolean>(false);
atomLoadingProcess.debugLabel = 'atomLoadingProcess';

export const atomRefetchAllData = atomWithReset<boolean>(false);
atomRefetchAllData.debugLabel = 'atomRefetchAllData';

export const atomRefetchDetailData = atomWithReset<boolean>(false);
atomRefetchDetailData.debugLabel = 'atomRefetchDetailData';

export const atomTotalsProcess = atomWithReset<ITotalsCampaign>({ total: 0, partial: 0 });
atomTotalsProcess.debugLabel = 'atomTotalsProcess';

export const atomDetailCampaign = atomWithReset<ICampaignDetail>({});
atomDetailCampaign.debugLabel = 'atomDetailCampaign';

export const atomLoadingDetail = atom<boolean>(false);
atomLoadingDetail.debugLabel = 'atomLoadingDetail';

export const atomCurrentCampaignID = atom<number>(0);
atomCurrentCampaignID.debugLabel = 'atomCurrentCampaignID';

export const atomSelectedRows = atom<number[]>([]);
atomSelectedRows.debugLabel = 'atomSelectedRows';

export const atomTotalSelectedRows = atom<number>((get) => {
  const selectedRows = get(atomSelectedRows);
  return selectedRows.length || 0;
});
atomTotalSelectedRows.debugLabel = 'atomTotalSelectedRows';

export const atomSelectedRowsWithID = atom((get) => {
  const selectedRowsWithID = get(atomSelectedRows)
    .map((index) => get(atomCampaignList)?.[index]?.campaign?.campaignId)
    .filter((campaignId) => campaignId !== undefined) as number[];
  return selectedRowsWithID;
});
atomSelectedRowsWithID.debugLabel = 'atomSelectedRowsWithID';

export const atomStrategyList = atom<IStrategy[]>([]);
atomStrategyList.debugLabel = 'atomStrategyList';

export const atomActionsList = atomWithReset<ITableCampaignActions[] | undefined>(undefined);
atomCampaignList.debugLabel = 'atomCampaignList';

export const atomTotalActionsSelectedRows = atom<number>((get) => {
  const selectedRows = get(atomSelectedRows);
  return selectedRows.length || 0;
});
atomTotalSelectedRows.debugLabel = 'atomTotalSelectedRows';

export const atomSelectedActionsRowsWithID = atom((get) => {
  const selectedActionsRowsWithID = get(atomSelectedRows)
    .map((index) => get(atomActionsList)?.[index]?.action?.actionId)
    .filter((actionId) => actionId !== undefined) as number[];
  return selectedActionsRowsWithID;
});
atomSelectedActionsRowsWithID.debugLabel = 'atomSelectedActionsRowsWithID';

export const atomSelectedActionsRowsHasMaster = atom((get) => {
  const hasMaster = get(atomSelectedRows).some(
    (index) => get(atomActionsList)?.[index]?.action?.channel === MASTER_ACTION
  );

  return hasMaster;
});

export const atomSelectedRowActionName = atom((get) => {
  const selectedRows = get(atomSelectedRows);
  const lastIndex = selectedRows.length - 1;
  const selectedActionName = get(atomActionsList)?.[selectedRows[lastIndex]]?.action?.name;
  return selectedActionName;
});
atomSelectedRowActionName.debugLabel = 'atomSelectedRowActionName';

export const atomDropdownCampaignList = atom((get): Option[] => {
  const currentCampaignID = get(atomCurrentCampaignID);
  const campaignListAll = get(atomCampaignListAll);
  if (!campaignListAll) return [] as Option[];

  const allCampaignsOptions = campaignListAll
    ?.filter((value) => {
      return value.campaign?.campaignTypeId === ECampaignsTypeOptions.MANUAL;
    })
    .map((value): Option => {
      const { campaignId, name } = value.campaign;
      return {
        id: campaignId,
        name: `${name}`,
        value: campaignId,
        disabled: campaignId === currentCampaignID,
      };
    });

  return allCampaignsOptions;
});
atomDropdownCampaignList.debugLabel = 'atomDropdownCampaignList';

export const atomMoveCampaignPayload = atomWithReset<IMoveCampaignPayload | undefined>(undefined);
atomMoveCampaignPayload.debugLabel = 'atomMoveCampaignPayload';

export const atomMoveSuccess = atomWithReset<boolean>(false);
atomMoveSuccess.debugLabel = 'atomMoveSuccess';

export const atomInfoCampaignV1 = atomWithReset<ICampaignDetailV1>({} as ICampaignDetailV1);
atomInfoCampaignV1.debugLabel = 'atomInfoCampaignV1';

export const atomShowSpinnerByAction = atomWithReset(true);
atomShowSpinnerByAction.debugLabel = 'atomShowSpinnerByAction';
